import { change } from 'redux-form';

// Insurance Claim Status Options - Single source of truth
export const INSURANCE_CLAIM_STATUS_OPTIONS = [
    { label: "hospital.billing.insurance_claims.form.status_draft", value: "DRAFT", color: "default" },
    { label: "hospital.billing.insurance_claims.form.status_submitted", value: "SUBMITTED", color: "info" },
    { label: "hospital.billing.insurance_claims.form.status_approved", value: "APPROVED", color: "success" },
    { label: "hospital.billing.insurance_claims.form.status_partially_approved", value: "PARTIALLY_APPROVED", color: "warning" },
    { label: "hospital.billing.insurance_claims.form.status_denied", value: "DENIED", color: "error" },
    { label: "hospital.billing.insurance_claims.form.status_paid", value: "PAID", color: "primary" }
];

// Insurance Claim Fields Configuration
// Following Sherwood-Fields patterns from the existing codebase

export const INSURANCE_CLAIM_FIELDS = {
    "organizationName": {
        id: "organizationName",
        required: true,
        type: "searchable_select",
        name: "organizationName",
        label: "hospital.billing.insurance_claims.form.organization_name",
        validation: "notEmpty",
        numberColumnsXs: 12,
        numberColumnsLg: 6,
        options: [] as Array<{label: string, value: string}>  // Will be populated dynamically
    },
    "status": {
        id: "status",
        required: true,
        type: "select-coloured",
        name: "status",
        label: "hospital.billing.insurance_claims.form.status",
        validation: "notEmpty",
        defaultOption: {"text": "hospital.billing.insurance_claims.form.select_status", "value": ""},
        options: INSURANCE_CLAIM_STATUS_OPTIONS,
        numberColumnsXs: 12,
        numberColumnsLg: 6,
        callBackOnChange: (value: string | number, allValues: any, dispatch: Function) => {
            // Clear approvedAmount if status is not APPROVED
            if (value !== "APPROVED" && dispatch) {
                dispatch(change('insuranceClaimForm', 'approvedAmount', undefined));
            }
        }
    },
    "coveragePercentage": {
        id: "coveragePercentage", 
        required: false,
        type: "number",
        name: "coveragePercentage",
        label: "hospital.billing.insurance_claims.form.coverage_percentage",
        numberColumnsXs: 12,
        numberColumnsLg: 6,
        callBackOnChange: (value: string | number, allValues: any, dispatch: Function) => {
            // Use the value from baseAmount field
            const baseAmount = parseFloat(allValues.baseAmount) || 0;
            const requestedAmount = (baseAmount * (parseFloat(value as string) || 0)) / 100;
            if (dispatch) {
                dispatch(change('form', 'requestedAmount', requestedAmount));
            }
        }
    },
    "baseAmount": {
        id: "requestedAmount",
        required: false,
        type: "hidden",
        name: "baseAmount", 
        label: "",
        validation: "number",
        numberColumnsXs: 12,
        numberColumnsLg: 6
    },
    "requestedAmount": {
        id: "requestedAmount",
        required: false,
        type: "label_value",
        name: "requestedAmount", 
        label: "hospital.billing.insurance_claims.form.requested_amount",
        validation: "number",
        numberColumnsXs: 12,
        numberColumnsLg: 6
    },
    "missingField": {
        id: "missingField",
        required: false,
        type: "hidden",
        name: "missingField",
        label: "",
        validation: "number",
        numberColumnsXs: 12,
        numberColumnsLg: 6
    },
    "approvedAmount": {
        id: "approvedAmount",
        required: false,
        type: "number",
        name: "approvedAmount",
        label: "hospital.billing.insurance_claims.form.approved_amount",
        validation: "number",
        numberColumnsXs: 12,
        numberColumnsLg: 6
    },
    "guaranteeLetterNumber": {
        id: "guaranteeLetterNumber",
        required: false,
        type: "text",
        name: "guaranteeLetterNumber",
        label: "hospital.billing.insurance_claims.form.guarantee_letter_number",
        numberColumnsXs: 12,
        numberColumnsLg: 12
    },
    "guaranteeLetterIssueDate": {
        id: "guaranteeLetterIssueDate",
        required: false,
        type: "date",
        name: "guaranteeLetterIssueDate",
        label: "hospital.billing.insurance_claims.form.guarantee_letter_issue_date",
        numberColumnsXs: 12,
        numberColumnsLg: 6
    },
    "interventionDate": {
        id: "interventionDate",
        required: false,
        type: "date", 
        name: "interventionDate",
        label: "hospital.billing.insurance_claims.form.intervention_date",
        numberColumnsXs: 12,
        numberColumnsLg: 6
    },
    "amountInLetters": {
        id: "amountInLetters",
        required: false,
        type: "text",
        name: "amountInLetters",
        label: "hospital.billing.insurance_claims.form.amount_in_letters",
        numberColumnsXs: 12,
        numberColumnsLg: 12
    },
    "guaranteeLetterPdfPath": {
        id: "guaranteeLetterPdfPath",
        required: false,
        type: "file_base64",
        name: "guaranteeLetterPdfPath",
        label: "hospital.billing.insurance_claims.form.guarantee_letter_pdf",
        numberColumnsXs: 12,
        numberColumnsLg: 6
    },
    // "approvalLetterImage": {
    //     id: "approvalLetterImage",
    //     required: false,
    //     type: "file_base64",
    //     name: "approvalLetterImage", 
    //     label: "hospital.billing.insurance_claims.form.approval_letter_image",
    //     numberColumnsXs: 12,
    //     numberColumnsLg: 6
    // },
    "notes": {
        id: "notes",
        required: false,
        type: "textarea",
        name: "notes",
        label: "hospital.billing.insurance_claims.form.notes",
        numberColumnsXs: 12,
        numberColumnsLg: 12
    }
};

// Helper function to populate insurance options
export const populateInsuranceOptions = (insurances: Array<{id: number, name: string}>) => {
    const insuranceOptions = insurances.map(insurance => ({
        label: insurance.name,
        value: insurance.id.toString()
    }));
    
    return INSURANCE_CLAIM_FIELDS;
};

// Helper function to populate organization options from backend organizations
export const populateOrganizationOptions = (organizations: Array<{id: number, name: string, uuid?: string}>) => {
    const organizationOptions = organizations.map(organization => ({
        label: organization.name,
        value: organization.id.toString()  // Store the ID as string (select field stores string values)
    }));
    
    // Mutate the fields object to add options
    INSURANCE_CLAIM_FIELDS.organizationName.options = organizationOptions;
    
    return INSURANCE_CLAIM_FIELDS;
};

// Field groups for better organization (optional)
export const FIELD_GROUPS = {
    REQUIRED_INFO: ['organizationName', 'status'],
    BASIC_INFO: ['coveragePercentage', 'requestedAmount', 'approvedAmount'],
    LETTER_INFO: ['guaranteeLetterNumber', 'guaranteeLetterIssueDate', 'interventionDate'],
    AMOUNT_DESCRIPTION: ['amountInLetters'],
    FILE_UPLOADS: ['guaranteeLetterPdf'], 
    ADDITIONAL_INFO: ['claimReference', 'notes']
};

// Validation messages (only for required fields)
export const VALIDATION_MESSAGES = {
    organizationName: 'hospital.billing.insurance_claims.validation.organization_name',
    status: 'hospital.billing.insurance_claims.validation.status'
}; 